import { FC, Ref, useState } from 'react';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';

import { ReservationMode, ScheduleWorkingHours, TreezStore } from '@/types';
import { useGapCommerceStore, useStoreHours } from '@/hooks';
import { filterResModAndProviderStoreId, getStoreScheduleInfo } from '@/utils';
import { Badge, Close, IconSvg } from '@/components';

import classNames from 'classnames';

import styles from './treezstore.module.scss';
import { usePopper } from 'react-popper';

interface IStoreRadioItemProps {
  store: TreezStore;
  isSelected?: boolean;
  isOpen?: boolean;
  onSelected: () => {};
  hiddeStatus?: boolean;
}

const StoreRadioItem: FC<IStoreRadioItemProps> = ({
  store,
  isSelected,
  onSelected,
  hiddeStatus,
}) => {
  const { store: gcStore } = useGapCommerceStore();
  const [button, setButton] = useState<HTMLButtonElement | undefined>();
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);

  const { styles: popperStyles, attributes } = usePopper(button, panel, {
    modifiers: [
      {
        name: 'offset',
        options: { offset: [16, 12] },
      },
    ],
    placement: 'top-end',
  });

  const pickup_schedules = gcStore?.pickup_schedules?.filter(
    s => s?.stores_provider_id === store?.shortName,
  );

  const closures = gcStore?.closures?.filter(s =>
    filterResModAndProviderStoreId(s, store?.shortName, ReservationMode.Pickup),
  );

  //const acceptingOrdersLabel = 'ACCEPTING ORDERS';

  const { isStoreOpen, storeStatusLabel, closureNote, isTemporarilyClosed } =
    useStoreHours({
      workingHours: store?.working_hours,
      gcWorkingHours: pickup_schedules?.[0]
        ?.working_hours as ScheduleWorkingHours[],
      closures: closures as any,
      timezone: gcStore?.timezone ?? store?.timezone,
    });

  const storeSchedule = getStoreScheduleInfo(
    pickup_schedules?.[0]?.working_hours as ScheduleWorkingHours[],
  );

  return (
    <div className={styles.store_radio_item} onClick={onSelected} role="button">
      <div className={styles.store_radio_header}>
        <input
          checked={isSelected}
          className={styles.store_radio}
          onChange={onSelected}
          type="radio"
        />
        <p>{`${store.name} - ${store.city}, ${store.state}`}</p>
      </div>
      <div className={styles.store_content}>
        <span className={styles.store_content_address}>
          {store.full_address.split(',')[0]}
        </span>
        {!hiddeStatus && (
          <div className={styles.store__status}>
            <div className={styles.store__status_content}>
              <Badge
                aria-live="polite"
                className={classNames(styles.store__status_content__badge, {
                  [styles.store__status_content__badge_closed]: !isStoreOpen,
                })}
                role="status"
              >
                {isStoreOpen ? 'OPEN' : 'CLOSED'}
              </Badge>
              <span className={styles.store__status_content__message}>
                {storeStatusLabel}
              </span>
            </div>
            <div className={styles.store__status_info}>
              {/*   {!isStoreOpen && !isTemporarilyClosed && (
                <Badge
                  aria-live="polite"
                  className={styles.store__status_info_badge}
                  role="status"
                >
                  {acceptingOrdersLabel}
                </Badge>
              )} */}
              <Popover className={styles.store__status_info__help}>
                <PopoverButton
                  aria-label="Show info"
                  ref={setButton as Ref<HTMLButtonElement>}
                >
                  <IconSvg name="alert-info" />
                </PopoverButton>
                <PopoverPanel
                  className={styles.store__status_info__help_body}
                  ref={setPanel as Ref<HTMLDivElement>}
                  style={{
                    ...popperStyles.popper,
                    zIndex: 9999,
                    maxWidth: '350px',
                  }}
                  {...attributes.popper}
                >
                  {({ close }) => (
                    <div className={styles.info__help_body}>
                      <div className={styles.peak}></div>
                      <div className={styles.popover_header}>
                        <h6 className={styles.popover_header__title}>
                          {isTemporarilyClosed
                            ? closureNote?.title ?? 'Temporarily Closed'
                            : 'Store Hours'}
                        </h6>
                        <Close
                          className={styles?.popover_header__close_btn}
                          onClick={e => {
                            e.stopPropagation();
                            close();
                          }}
                        />
                      </div>
                      <div className={styles.popover_body}>
                        {isTemporarilyClosed ? (
                          <p>{closureNote?.message}</p>
                        ) : (
                          <>
                            {storeSchedule?.length > 0
                              ? storeSchedule?.map((el, index) => {
                                  return (
                                    <div
                                      className={
                                        styles.popover_body_store_schedule_container
                                      }
                                      key={`${el?.day}_${index}`}
                                    >
                                      <span>{el?.day}</span>
                                      <span>{el?.period}</span>
                                    </div>
                                  );
                                })
                              : 'This store hasn’t provided its operating hours yet.'}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </PopoverPanel>
              </Popover>
            </div>
          </div>
        )}
        {/*  <span
          className={classNames(styles.status, {
            [styles.closed]: !isOpen,
          })}
        >
          {isOpen ? 'Open now' : 'Closed'}
        </span> */}
      </div>
    </div>
  );
};

export { StoreRadioItem };
